import { setOnStorage, getOnStorage, removeFromStorage } from '@/shared';
import { createContext, useMemo, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useClient } from './ClientContext';
import { useCorporateFlags } from './CorporateFlagsContext';

export interface StockProps {
  stock_left?: number;
  initial_stock?: number;
  last_sync_date?: string;
  date_update_stock_dataset?: string;
  date_update_local_stock?: string;
}

interface StockControlProps {
  product_code: number;
  stock: StockProps;
}

interface HookProps {
  stockLeft?: Record<number, StockProps>;
  /**
   * Código da loja ativa derivado do `selectedBrandPair` validado contra `selectedClient.lojas`.
   * É a única fonte de verdade para escopar estoque/limites por loja — outros contextos devem
   * consumir este valor em vez de `selectedClient.COD_LOJA` (que é a loja raiz, não a ativa).
   */
  activeStoreCode: number;
  handleUpdateStock: (params: StockControlProps) => void;
  handleClearStock: () => void;
  triggerCompareStocks: () => void;
}

const StockContext = createContext<HookProps>({} as HookProps);
const EMPTY_STOCK_BY_PRODUCT: Record<number, StockProps> = {};

const StockProvider: React.FC<any> = ({ children }) => {
  const { selectedClient } = useClient();
  const { selectedBrandPair } = useCorporateFlags();
  const [shouldUpdate, setShouldUpdate] = useState(false);
  
  const [hasSynched, setHasSynched] = useState(false);
  
  const [stockLeftContext, setStockLeft] = useState(() => {
    const stock = getOnStorage('stock_left');
    if (stock) {
      return stock as Record<number, Record<number, StockProps>>;
    }
    return {} as Record<number, Record<number, StockProps>>;
  });

  const activeStoreCode = useMemo(() => {
    // A troca de bandeira pode acontecer antes de toda hidratação do cliente (ex.: voltar do carrinho).
    // Nesses casos, validar o pair contra `selectedClient.lojas` aqui pode cair em fallback para loja raiz
    // e deixar todos os produtos "indisponíveis" temporariamente. A bandeira já vem validada no provider.
    const selectedBrandStoreCode = Number(selectedBrandPair?.codLoja);
    if (Number.isFinite(selectedBrandStoreCode) && selectedBrandStoreCode > 0) {
      return selectedBrandStoreCode;
    }

    const selectedClientStoreCode = Number(selectedClient?.COD_LOJA);
    if (Number.isFinite(selectedClientStoreCode) && selectedClientStoreCode > 0) {
      return selectedClientStoreCode;
    }

    const storageStoreCode = Number(getOnStorage('selected_client')?.COD_LOJA);
    return Number.isFinite(storageStoreCode) && storageStoreCode > 0 ? storageStoreCode : 0;
  }, [selectedBrandPair?.codLoja, selectedClient?.COD_LOJA]);

  const stockLeft = useMemo(() => {
    return stockLeftContext?.[activeStoreCode] || EMPTY_STOCK_BY_PRODUCT;
  }, [stockLeftContext, activeStoreCode]);

  const stockRef = useRef({} as Record<number, StockProps>);

  // Atualiza o estoque no storage
  const handleUpdateStock = useCallback(({ product_code, stock }: StockControlProps) => {
    const codLoja = activeStoreCode || Number(getOnStorage('selected_client')?.COD_LOJA) || 0;
    let stockCurrent = { ...stockLeftContext };
    if (!stockCurrent?.[codLoja]) stockCurrent = { ...stockCurrent, [codLoja]: {} };
    stockCurrent[codLoja] = { ...stockCurrent[codLoja], [product_code]: stock };
    setStockLeft(stockCurrent);
    setShouldUpdate(true);
    setOnStorage('stock_left', stockCurrent);
  }, [activeStoreCode, stockLeftContext]);

  // Remove os dados de estoque do storage
  const handleClearStock = useCallback(() => {
    removeFromStorage('stock_left');
    setStockLeft({});
  }, []);

  const handleCompareStocks = useCallback(() => {
    const now = new Date();

    const datasetStockLastUpdate = getOnStorage('datasets')?.find((dataset) => dataset?.TIPO_ARQUIVO === 'estoque');

    if (datasetStockLastUpdate?.DT_UPDATE) {
      console.warn('datas: ', { now, datasetupdate: datasetStockLastUpdate?.DT_UPDATE, diff: now?.getTime() - Date.parse(datasetStockLastUpdate?.DT_UPDATE) > 0 ? 'Versão local tá mais atual' : 'Versão local tá mais antiga' });
    }

  }, [stockLeft]);

  const triggerCompareStocks = useCallback(() => {
    setHasSynched(true);
  }, []);

  useEffect(() => {
    if (hasSynched) {
      handleCompareStocks();
    }
  }, [hasSynched, handleCompareStocks]);

  useEffect(() => {
    if (stockRef.current !== stockLeft) {
      stockRef.current = stockLeft;
    }
  }, [stockLeft]);

  const context = useMemo(() => ({
    stockLeft,
    activeStoreCode,
    handleUpdateStock,
    handleClearStock,
    triggerCompareStocks,
  }), [stockLeft, activeStoreCode, handleUpdateStock, handleClearStock, triggerCompareStocks]) as HookProps;

  return (
    <StockContext.Provider value={context}>
      {children}
    </StockContext.Provider>
  );
};

const useStock = (): HookProps => {
  const context = useContext(StockContext);

  if (!context) throw new Error('Erro ao usar o Stock Hook');
  return context;
};

export { StockProvider, useStock };
